import { has, head, isEmpty, pick } from 'lodash-es';
import * as types from './mutations-types';
import { getDistanceFromLatLngInKm } from '../helpers/store-helper';
import { clientApi } from '@phoenix/clientApi';

const CancelToken = clientApi.getCancelToken();
let cancelSuggestionRequest;
let cancelLoadStoresRequest;

const initialState = {
  keyword: '',
  position: {},
  placeId: null,
  boutiqueOnly: false,
  viewport: {},
  isResultsComponentPresent: false,
  currentTab: 'map',
  isLoading: false,
  isSuggestionLoading: false,
  stores: [],
  fitBoundStores: [],
  allStores: [],
  placeSuggestions: [],
  fetchedPlace: null,
  boundsChanged: false,
  shouldListenToBoundsChange: false,
};

const getters = {
  hasResults: (state) => state.stores.length > 0,
  customBounds: (state) => {
    const bounds = [];

    if (isEmpty(state.position) || state.stores.length === 0) {
      if (!isEmpty(state.fitBoundStores)) {
        return state.fitBoundStores
          .filter((store) => store.lat !== 0 && store.lng !== 0)
          .map((store) => ({
            lat: store.lat,
            lng: store.lng,
          }));
      }

      return bounds;
    }

    // Since we have a precise lat & lng, we want to add it to the bound
    bounds.push(state.position);

    if (state.placeId !== null && has(state, ['viewport', 'southWest']) && has(state, ['viewport', 'northEast'])) {
      // Google place viewport gives us two points (diagonal of a 'rectangle') and we're looking at all stores that
      // are between those two points
      bounds.push(
        ...state.stores.filter(
          (store) =>
            store.lat > state.viewport.southWest.lat &&
            store.lat < state.viewport.northEast.lat &&
            store.lng > state.viewport.southWest.lng &&
            store.lng < state.viewport.northEast.lng,
        ),
      );

      // If at this point we only have one bound it means that there are no shops inside the place view port
      // so we're adding to the bound the closest store
      if (bounds.length === 1 && head(state.stores) !== undefined) {
        bounds.push({
          lat: state.stores[0].lat,
          lng: state.stores[0].lng,
        });
      }
    } else if (head(state.stores) !== undefined) {
      // If we only have a lat & lng (meaning the customer used 'use my location' we'll show the closest store found
      // to the bound
      bounds.push({
        lat: state.stores[0].lat,
        lng: state.stores[0].lng,
      });
    }

    return bounds;
  },
  searchPerformed: (state) => !isEmpty(state.keyword) || !isEmpty(state.position) || !isEmpty(state.placeId),
};

const actions = {
  /**
   * Set the query we are looking for
   */
  setKeyword({ commit, dispatch, state }, keyword) {
    if (state.keyword === keyword && !state.boundsChanged) {
      return;
    }

    commit(types.STORE_LOCATOR_SET_KEYWORD, keyword);

    dispatch('loadData');
  },

  /**
   * Set the boutique only flag
   */
  setBoutiqueOnly({ commit }, boutiqueOnly) {
    commit(types.STORE_LOCATOR_SET_BOUTIQUE_ONLY, boutiqueOnly);
  },

  /**
   * Set the query we are looking for
   */
  setPosition({ commit, dispatch, state }, position) {
    position = {
      lat: Number(position.lat),
      lng: Number(position.lng),
    };

    if (
      !isEmpty(state.position) &&
      state.position.lat === position.lat &&
      state.position.lng === position.lng &&
      !state.boundsChanged
    ) {
      return;
    }

    commit(types.STORE_LOCATOR_SET_POSITION, position);

    dispatch('loadData');
  },

  async setPlace({ commit, dispatch }, placeId) {
    commit(types.STORE_LOCATOR_SET_LOADING, { isLoading: true });
    commit(types.STORE_LOCATOR_SET_PLACE_ID, placeId);

    try {
      const response = await clientApi.get(window.route('api.search.stores.placeDetails', { placeId }));

      const geometry = response.data.data.response.result.geometry;

      commit(types.STORE_LOCATOR_SET_FETCHED_PLACE, response.data.data.response.result);

      commit(types.STORE_LOCATOR_SET_POSITION, {
        lat: geometry.location.lat,
        lng: geometry.location.lng,
      });

      if (has(geometry, 'viewport.northeast') && has(geometry, 'viewport.southwest')) {
        commit(types.STORE_LOCATOR_SET_VIEWPORT, {
          northEast: {
            lat: geometry.viewport.northeast.lat,
            lng: geometry.viewport.northeast.lng,
          },
          southWest: {
            lat: geometry.viewport.southwest.lat,
            lng: geometry.viewport.southwest.lng,
          },
        });
      }

      await dispatch('loadData');
    } catch (error) {
      window.Phoenix.sentry.captureException(error);
      throw error;
    }
    commit(types.STORE_LOCATOR_SET_LOADING, { isLoading: false });
  },

  async fetchSuggestions({ commit }, input) {
    commit(types.STORE_LOCATOR_SET_PLACE_SUGGESTIONS_LOADING, { isLoading: true });

    try {
      if (cancelSuggestionRequest) {
        cancelSuggestionRequest();
      }

      const response = await clientApi.get(window.route('api.search.stores.placeAutocomplete', { input }), {
        cancelToken: new CancelToken((c) => {
          cancelSuggestionRequest = c;
        }),
      });

      commit(
        types.STORE_LOCATOR_SET_PLACE_SUGGESTIONS,
        response.data.data.response.predictions.map((prediction) => {
          const secondaryText = prediction.structured_formatting.secondary_text;
          const formattedSuggestion = [prediction.structured_formatting.main_text];
          if (secondaryText) {
            formattedSuggestion.push(`<span class="text-muted">${secondaryText}</span>`);
          }
          return {
            key: prediction.place_id,
            suggestion: prediction.description,
            formattedSuggestion: formattedSuggestion.join(', '),
          };
        }),
      );
    } catch (error) {
      if (clientApi.isCancel(error)) {
        // The request has been canceled, do nothing
      } else {
        window.Phoenix.sentry.captureException(error);
        throw error;
      }
    }

    commit(types.STORE_LOCATOR_SET_PLACE_SUGGESTIONS_LOADING, { isLoading: false });
  },

  async loadData({ commit, state }) {
    if (isEmpty(state.keyword) && isEmpty(state.position)) {
      return;
    }

    commit(types.STORE_LOCATOR_SET_LOADING, {
      isLoading: true,
    });
    commit(types.STORE_LOCATOR_SET_BOUNDS_CHANGED, false);
    commit(types.STORE_LOCATOR_SET_SHOULD_LISTEN_TO_BOUNDS_CHANGE, false);

    const params = {
      place: state.fetchedPlace ? pick(state.fetchedPlace, ['address_components', 'types']) : null,
    };

    if (!isEmpty(state.keyword)) {
      params.keyword = state.keyword;
    }

    params.boutiqueOnly = state.boutiqueOnly;

    if (!isEmpty(state.position)) {
      params.lat = state.position.lat;
      params.lng = state.position.lng;
      if (!isEmpty(state.placeId) && has(state, 'viewport.northEast')) {
        params.distance = getDistanceFromLatLngInKm(
          params.lat,
          params.lng,
          state.viewport.northEast.lat,
          state.viewport.northEast.lng,
        );
      }
    }

    try {
      if (cancelLoadStoresRequest) {
        cancelLoadStoresRequest();
      }

      const { data } = await clientApi.get(window.route('api.search.stores.locator'), {
        params,
        cancelToken: new CancelToken((c) => {
          cancelLoadStoresRequest = c;
        }),
      });

      commit(types.STORE_LOCATOR_SET_RESULTS, { data: data.data });
    } catch (error) {
      if (!clientApi.isCancel(error)) {
        window.Phoenix.sentry.captureException(error);
      }
    }

    commit(types.STORE_LOCATOR_SET_LOADING, {
      isLoading: false,
    });
  },

  async loadStoresOnBoundsChanged({ state, commit }, bounds) {
    const distance = getDistanceFromLatLngInKm(
      bounds.center.lat,
      bounds.center.lng,
      bounds.northEast.lat,
      bounds.northEast.lng,
    );

    if (distance === 0) {
      return;
    }

    if (!state.shouldListenToBoundsChange) {
      commit(types.STORE_LOCATOR_SET_SHOULD_LISTEN_TO_BOUNDS_CHANGE, true);
      return;
    }

    commit(types.STORE_LOCATOR_SET_BOUNDS_CHANGED, true);

    const params = {
      lat: bounds.center.lat,
      lng: bounds.center.lng,
      distance,
      boutiqueOnly: state.boutiqueOnly,
      boundsChanged: true,
    };

    try {
      if (cancelLoadStoresRequest) {
        cancelLoadStoresRequest();
      }

      const { data } = await clientApi.get(window.route('api.search.stores.locator'), {
        params,
        cancelToken: new CancelToken((c) => {
          cancelLoadStoresRequest = c;
        }),
      });

      commit(types.STORE_LOCATOR_SET_RESULTS, { data: data.data });
    } catch (error) {
      if (!clientApi.isCancel(error)) {
        window.Phoenix.sentry.captureException(error);
      }
    }
  },

  /**
   * Used by the Search store form to know if it should redirect to the search page or just refresh the store
   * @param commit
   */
  resultComponentIsPresent({ commit }) {
    commit(types.STORE_LOCATOR_SET_RESULT_COMPONENT_PRESENCE, true);
  },

  changeCurrentTab({ commit }, tab) {
    commit(types.STORE_LOCATOR_CHANGE_CURRENT_TAB, tab);
  },

  resetPlaceId({ commit }) {
    commit(types.STORE_LOCATOR_RESET_PLACE_ID);
  },

  setStores({ commit }, data) {
    commit(types.STORE_LOCATOR_SET_RESULTS, { data });
  },

  setFitBoundStores({ commit }, data) {
    commit(types.STORE_LOCATOR_SET_FIT_BOUND_STORES, { data });
  },

  resetResults({ commit }) {
    commit(types.STORE_LOCATOR_RESET_RESULTS);
  },
};

const mutations = {
  [types.STORE_LOCATOR_SET_KEYWORD](state, keyword) {
    state.keyword = keyword;
    state.position = null;
  },

  [types.STORE_LOCATOR_SET_POSITION](state, { lat, lng }) {
    state.keyword = '';
    state.position = {
      lat,
      lng,
    };
  },

  [types.STORE_LOCATOR_RESET_RESULTS](state) {
    state.stores = [];
  },

  [types.STORE_LOCATOR_SET_LOADING](state, { isLoading }) {
    state.isLoading = isLoading;
  },

  [types.STORE_LOCATOR_SET_PLACE_SUGGESTIONS_LOADING](state, { isLoading }) {
    state.isSuggestionLoading = isLoading;
  },

  [types.STORE_LOCATOR_SET_RESULTS](state, { data }) {
    state.stores = data;
  },

  [types.STORE_LOCATOR_SET_FIT_BOUND_STORES](state, { data }) {
    state.fitBoundStores = data;
  },

  [types.STORE_LOCATOR_SET_ALL_STORES](state, { data }) {
    state.allStores = data;
  },

  [types.STORE_LOCATOR_SET_RESULT_COMPONENT_PRESENCE](state, value) {
    state.isResultsComponentPresent = value;
  },

  [types.STORE_LOCATOR_CHANGE_CURRENT_TAB](state, tab) {
    state.currentTab = tab;
  },

  [types.STORE_LOCATOR_SET_PLACE_ID](state, placeId) {
    state.placeId = placeId;
  },

  [types.STORE_LOCATOR_SET_VIEWPORT](state, viewport) {
    state.viewport = viewport;
  },

  [types.STORE_LOCATOR_SET_PLACE_SUGGESTIONS](state, suggestions) {
    state.placeSuggestions = suggestions;
  },

  [types.STORE_LOCATOR_SET_FETCHED_PLACE](state, data) {
    state.fetchedPlace = data;
  },

  [types.STORE_LOCATOR_RESET_PLACE_ID](state) {
    state.placeId = null;
  },

  [types.STORE_LOCATOR_SET_BOUTIQUE_ONLY](state, boutiqueOnly) {
    state.boutiqueOnly = boutiqueOnly;
  },

  [types.STORE_LOCATOR_SET_BOUNDS_CHANGED](state, boundsChanged) {
    state.boundsChanged = boundsChanged;
  },

  [types.STORE_LOCATOR_SET_SHOULD_LISTEN_TO_BOUNDS_CHANGE](state, shouldListenToBoundsChange) {
    state.shouldListenToBoundsChange = shouldListenToBoundsChange;
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
